import { useEffect, useId, useRef, useState } from 'react';
import {
  REFERENCE_CATEGORY_LABELS,
  REFERENCE_LINK_TARGET_LABELS,
  ReferenceCategory,
  type ReferenceCard,
  type ReferenceCategory as Category,
  type ReferenceGroupView,
  type ReferenceLibraryMeta,
  type ReferenceSort,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SearchField } from '../ui/SearchField.js';
import { SegmentedFilter, type FilterOption } from '../ui/SegmentedFilter.js';
import { EmptyLibrary, type AddMode } from './EmptyLibrary.js';
import { ReferenceCardView, ReferenceSkeletons } from './ReferenceCard.js';
import { referenceShareUrl } from './ReferenceDetailPanel.js';
import { hasNarrowing, libraryQueryFrom, type ReferenceAddress, type ReferenceAddressChange } from './address.js';
import { errorText, useDebounced, useReferenceLibrary } from './hooks.js';

type ChipId = 'all' | 'linked' | 'discussed' | 'none' | `cat-${Category}`;

const SORT_LABELS: Readonly<Record<ReferenceSort, string>> = Object.freeze({
  RECENT: 'Recently added',
  UPDATED: 'Last updated',
  TITLE: 'Title A–Z',
});

/**
 * THE LIBRARY ITSELF — the centre column: search, the chips (All · each
 * category present · Linked · With discussion, counted by the server), sort, the
 * card or list view, and "Load more" on the server's cursor. Every one of those
 * is the address's (`useReferenceAddress`), so a reload shows the same list.
 *
 * Four different "nothing here"s, because each sends somebody somewhere else:
 * an empty library (start one), an empty group (put something in it), a search
 * that matched nothing, and filters that matched nothing.
 */
export function LibraryPane({
  projectId,
  address,
  update,
  meta,
  group,
  canEdit,
  refusal,
  onAdd,
  onOrganize,
  onAddExisting,
  onOpenGroups,
}: {
  projectId: string;
  address: ReferenceAddress;
  update: (change: ReferenceAddressChange, options?: { push?: boolean }) => void;
  meta: ReferenceLibraryMeta | undefined;
  group: ReferenceGroupView | null;
  canEdit: boolean;
  refusal: string | null;
  onAdd: (mode: AddMode) => void;
  onOrganize: () => void;
  onAddExisting: () => void;
  onOpenGroups: () => void;
}) {
  const library = useReferenceLibrary(projectId, libraryQueryFrom(address));
  const cards = (library.data?.pages ?? []).flatMap((p) => p.items);
  const total = library.data?.pages[0]?.total ?? null;
  const [status, setStatus] = useState<string | null>(null);
  const sortId = useId();

  // The search box types freely; the address (and the request) follow once it settles.
  const [draft, setDraft] = useState(address.q);
  const settled = useDebounced(draft, 300);
  const written = useRef(address.q);
  useEffect(() => {
    if (settled.trim() === written.current.trim()) return;
    written.current = settled;
    update({ q: settled });
  }, [settled, update]);
  useEffect(() => {
    if (address.q.trim() === written.current.trim()) return;
    written.current = address.q;
    setDraft(address.q);
  }, [address.q]);

  const libraryEmpty = meta !== undefined && meta.total === 0;

  const chipValue: ChipId = address.discussed
    ? 'discussed'
    : address.linked === 'ANY'
      ? 'linked'
      : address.cat !== null
        ? `cat-${address.cat}`
        : address.linked !== null
          ? 'none'
          : 'all';

  const present = ReferenceCategory.options.filter(
    (c) => (meta?.counts.byCategory[c] ?? 0) > 0 || address.cat === c,
  );
  const chips: FilterOption<ChipId>[] = [
    { id: 'all', label: 'All', ...(meta ? { count: meta.total } : {}) },
    ...present.map((c) => ({
      id: `cat-${c}` as const,
      label: REFERENCE_CATEGORY_LABELS[c],
      ...(meta ? { count: meta.counts.byCategory[c] ?? 0 } : {}),
    })),
    { id: 'linked', label: 'Linked', ...(meta ? { count: meta.counts.linked } : {}) },
    ...(meta === undefined || meta.counts.discussed === null
      ? []
      : [{ id: 'discussed' as const, label: 'With discussion', count: meta.counts.discussed }]),
  ];

  const choose = (id: ChipId) => {
    if (id === 'all' || id === 'none') update({ cat: null, linked: null, discussed: null });
    else if (id === 'linked') update({ linked: 'ANY', cat: null, discussed: null });
    else if (id === 'discussed') update({ discussed: true, cat: null, linked: null });
    else update({ cat: id.slice(4) as Category, linked: null, discussed: null });
  };

  // What the item a "References · n" entry pointed at is called, from a card that is linked to it.
  const linkedLabel =
    address.linked !== null && address.linked !== 'ANY' && address.linkedId !== null
      ? (cards
          .flatMap((c) => c.links)
          .find((l) => l.targetType === address.linked && l.targetId === address.linkedId)?.label ?? null)
      : null;

  const copy = async (card: ReferenceCard) => {
    const url = referenceShareUrl(projectId, card.id);
    try {
      await navigator.clipboard.writeText(url);
      setStatus('Link copied.');
    } catch {
      setStatus(`Copy this link: ${url}`);
    }
  };

  return (
    <div className="ref-library-inner" data-testid="ref-library">
      {libraryEmpty ? null : (
        <div className="ref-toolbar">
          <div className="ref-toolbar-row">
            <button type="button" className="ctl ref-groups-toggle" data-testid="ref-groups-open" onClick={onOpenGroups}>
              <Icon name="folder" size={15} />
              {group === null ? 'Groups' : group.name}
            </button>
            <div className="ref-search">
              <SearchField
                value={draft}
                onChange={setDraft}
                placeholder="Search references"
                label="Search references"
                testId="ref-search"
              />
            </div>
            <div className="ref-toolbar-end">
              <label className="ref-sort" htmlFor={sortId}>
                <span>Sort</span>
                <select
                  id={sortId}
                  className="ref-select"
                  value={address.sort}
                  data-testid="ref-sort"
                  onChange={(event) => {
                    update({ sort: event.target.value as ReferenceSort });
                  }}
                >
                  {(['RECENT', 'UPDATED', 'TITLE'] as const).map((s) => (
                    <option key={s} value={s}>
                      {SORT_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="ref-view-toggle" role="group" aria-label="View">
                <button
                  type="button"
                  className="ref-view-btn"
                  aria-pressed={address.view === 'grid'}
                  aria-label="Show as cards"
                  title="Cards"
                  data-testid="ref-view-grid"
                  onClick={() => {
                    update({ view: 'grid' });
                  }}
                >
                  <Icon name="grid" size={16} />
                </button>
                <button
                  type="button"
                  className="ref-view-btn"
                  aria-pressed={address.view === 'list'}
                  aria-label="Show as a list"
                  title="List"
                  data-testid="ref-view-list"
                  onClick={() => {
                    update({ view: 'list' });
                  }}
                >
                  <Icon name="list" size={16} />
                </button>
              </div>
            </div>
          </div>
          <SegmentedFilter options={chips} value={chipValue} onChange={choose} label="Show" testIdPrefix="ref-filter" />
          {group !== null || (address.linked !== null && address.linked !== 'ANY') ? (
            <ul className="ref-active-filters" aria-label="Narrowed to">
              {group !== null ? (
                <li className="ref-chip" data-tone="group" data-testid="ref-active-group">
                  <Icon name="folder" size={12} />
                  <span>{group.name}</span>
                  <button
                    type="button"
                    className="ref-chip-x"
                    aria-label={`Show all references, not only ${group.name}`}
                    data-testid="ref-active-group-clear"
                    onClick={() => {
                      update({ group: null });
                    }}
                  >
                    <Icon name="close" size={12} />
                  </button>
                </li>
              ) : null}
              {address.linked !== null && address.linked !== 'ANY' ? (
                <li className="ref-chip" data-tone="link" data-testid="ref-active-linked">
                  <span>
                    {`Linked to ${linkedLabel ?? `this ${REFERENCE_LINK_TARGET_LABELS[address.linked].toLowerCase()}`}`}
                  </span>
                  <button
                    type="button"
                    className="ref-chip-x"
                    aria-label="Show references linked to anything"
                    data-testid="ref-active-linked-clear"
                    onClick={() => {
                      update({ linked: null });
                    }}
                  >
                    <Icon name="close" size={12} />
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      )}

      <p className="sr-only" role="status" aria-live="polite" data-testid="ref-results-status">
        {total === null ? '' : `${String(total)} reference${total === 1 ? '' : 's'}`}
      </p>
      {status !== null ? (
        <p className="ref-quiet" role="status" data-testid="ref-library-status">
          {status}
        </p>
      ) : null}

      {libraryEmpty ? (
        <EmptyLibrary canEdit={canEdit} refusal={refusal} onAdd={onAdd} onOrganize={onOrganize} />
      ) : library.isPending ? (
        <ReferenceSkeletons view={address.view} />
      ) : library.isError ? (
        <EmptyState
          testId="ref-library-error"
          title="The references could not be loaded"
          action={
            <button
              type="button"
              className="ctl"
              data-testid="ref-library-retry"
              onClick={() => {
                void library.refetch();
              }}
            >
              Try again
            </button>
          }
        >
          {errorText(library.error, 'Nothing is lost — this is a problem reaching the server.')}
        </EmptyState>
      ) : cards.length === 0 ? (
        <NothingHere
          address={address}
          group={group}
          canEdit={canEdit}
          onAdd={onAdd}
          onAddExisting={onAddExisting}
          onClearSearch={() => {
            setDraft('');
            written.current = '';
            update({ q: null });
          }}
          onClearFilters={() => {
            update({ cat: null, linked: null, discussed: null });
          }}
        />
      ) : (
        <>
          <h3 className="sr-only">References</h3>
          <ul className="ref-results" data-view={address.view} data-testid="ref-results">
            {cards.map((card) => (
              <li key={card.id}>
                <ReferenceCardView
                  card={card}
                  view={address.view}
                  selected={address.ref === card.id}
                  onOpen={() => {
                    update({ ref: card.id }, { push: true });
                  }}
                  menu={[
                    {
                      id: 'open',
                      label: 'Open',
                      testId: `ref-card-menu-${card.id}-open`,
                      onSelect: () => {
                        update({ ref: card.id }, { push: true });
                      },
                    },
                    {
                      id: 'copy',
                      label: 'Copy link',
                      testId: `ref-card-menu-${card.id}-copy`,
                      onSelect: () => {
                        void copy(card);
                      },
                    },
                  ]}
                />
              </li>
            ))}
          </ul>
          {library.hasNextPage ? (
            <div className="ref-more">
              <span className="ref-quiet">{`${String(cards.length)} of ${String(total ?? cards.length)}`}</span>
              <button
                type="button"
                className="ctl"
                disabled={library.isFetchingNextPage}
                data-testid="ref-load-more"
                onClick={() => {
                  void library.fetchNextPage();
                }}
              >
                {library.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function NothingHere({
  address,
  group,
  canEdit,
  onAdd,
  onAddExisting,
  onClearSearch,
  onClearFilters,
}: {
  address: ReferenceAddress;
  group: ReferenceGroupView | null;
  canEdit: boolean;
  onAdd: (mode: AddMode) => void;
  onAddExisting: () => void;
  onClearSearch: () => void;
  onClearFilters: () => void;
}) {
  if (address.q.trim() !== '') {
    return (
      <EmptyState
        testId="ref-empty-search"
        title={`Nothing matches “${address.q.trim()}”`}
        action={
          <button type="button" className="ctl" data-testid="ref-empty-search-clear" onClick={onClearSearch}>
            Clear the search
          </button>
        }
      >
        The search looks at titles, descriptions, notes, links, tags and group names.
      </EmptyState>
    );
  }
  if (group !== null && !hasNarrowing(address)) {
    return (
      <EmptyState
        testId="ref-empty-group"
        title="No references in this group yet."
        action={
          canEdit ? (
            <div className="ref-empty-actions">
              <button type="button" className="ctl" data-testid="ref-empty-group-existing" onClick={onAddExisting}>
                Add existing references
              </button>
              <button
                type="button"
                className="ctl"
                data-testid="ref-empty-group-new"
                onClick={() => {
                  onAdd('LINK');
                }}
              >
                Add a new reference
              </button>
            </div>
          ) : undefined
        }
      />
    );
  }
  return (
    <EmptyState
      testId="ref-empty-filter"
      title="No references match these filters"
      action={
        <button type="button" className="ctl" data-testid="ref-empty-filter-clear" onClick={onClearFilters}>
          Show all references
        </button>
      }
    />
  );
}
